/**
 * A small RFC 4180 CSV parser.
 *
 * WHY NOT A LIBRARY
 * -----------------
 * The project has no CSV dependency, and this is forty lines. Adding one for
 * this alone would be a larger change than the parser itself.
 *
 * WHY NOT split(',')
 * ------------------
 * Because real school rosters break it on the first file. Every one of the
 * cases below has been seen in an exported roster:
 *
 *   "Sharma, Aarti",11,A          a comma inside a quoted field
 *   "St. Mary""s High",...        an escaped quote
 *   "12 MG Road\nJaipur",...      a newline inside a quoted field
 *   ﻿Email,Name,...          a BOM, from Excel's "CSV UTF-8"
 *   Email,Name\r\n                CRLF line endings
 *
 * A naive split turns the first row into a student called `"Sharma` in class
 * ` Aarti"`, and the administrator gets an error about a missing section that
 * has nothing to do with what is wrong.
 *
 * Returns rows as objects keyed by the header line. Header matching itself is
 * done on the server, forgivingly -- see Roster_importer::$ALIASES -- so this
 * only has to hand back what the file actually said.
 */

/** One pass, character by character, tracking whether we are inside quotes. */
const splitRows = (text: string): string[][] => {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = '';
    let inQuotes = false;

    // Excel's "CSV UTF-8" writes a byte-order mark. Left in place it becomes
    // part of the first header's name, so the first column silently stops
    // matching anything.
    if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

    const endField = () => {
        row.push(field);
        field = '';
    };
    const endRow = () => {
        endField();
        // Skip the blank row that a trailing newline always produces, and any
        // stray blank lines in the middle of the file.
        if (row.length > 1 || row[0] !== '') rows.push(row);
        row = [];
    };

    for (let i = 0; i < text.length; i++) {
        const c = text[i];

        if (inQuotes) {
            if (c === '"') {
                // A doubled quote inside quotes is a literal quote.
                if (text[i + 1] === '"') {
                    field += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                field += c;
            }
            continue;
        }

        if (c === '"') {
            inQuotes = true;
        } else if (c === ',') {
            endField();
        } else if (c === '\n') {
            endRow();
        } else if (c === '\r') {
            // CRLF: the \n that follows does the work.
        } else {
            field += c;
        }
    }

    if (field !== '' || row.length > 0) endRow();
    return rows;
};

export type ParsedCsv = {
    headers: string[];
    rows: Record<string, string>[];
    /** Problems with the file as a whole, not with any one row. */
    problem?: string;
};

export const parseCsv = (text: string): ParsedCsv => {
    const raw = splitRows(text);

    if (raw.length === 0) {
        return { headers: [], rows: [], problem: 'That file is empty.' };
    }

    const headers = raw[0].map(h => h.trim());

    if (raw.length === 1) {
        return {
            headers,
            rows: [],
            problem:
                'That file has a header line but no rows under it. Check you exported the data ' +
                'and not just the column titles.',
        };
    }

    const rows: Record<string, string>[] = [];

    for (let i = 1; i < raw.length; i++) {
        const cells = raw[i];

        // A row that is entirely empty is skipped rather than reported. Files
        // exported from spreadsheets routinely end with a few of these, and
        // complaining about them helps nobody.
        if (cells.every(c => c.trim() === '')) continue;

        const obj: Record<string, string> = {};
        headers.forEach((h, j) => {
            if (h !== '') obj[h] = (cells[j] ?? '').trim();
        });
        rows.push(obj);
    }

    return { headers, rows };
};

/** Build a CSV for download -- the password list, and the error report. */
export const toCsv = (headers: string[], rows: (string | number | null)[][]): string => {
    const cell = (v: string | number | null) => {
        const s = v === null || v === undefined ? '' : String(v);
        // Quote whenever the value could otherwise be misread on the way back in.
        return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    return [headers, ...rows].map(r => r.map(cell).join(',')).join('\r\n');
};

export const downloadCsv = (filename: string, csv: string) => {
    // The BOM is deliberate: without it Excel opens a UTF-8 CSV as the system
    // codepage and mangles every non-ASCII name in the file.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};
